import axios from "axios";
import { useEffect, useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { API } from "./shared";
import { AcPartnerApps, AcKycQueue, AcPendingPayouts } from "./AdminInbox";
import { AcFixJobOwnership, AcAuditLog, AcSamplePacks, AcSettlements, AcPayments, AcPlatformStats } from "./AdminOps";
import { AcSupport } from "./Support";
import { DisputesView } from "./Disputes";
import { AdminConversations } from "./AdminInbox";
import { AcTestAccounts, IncludeTestToggle } from "./TestAccounts";
import { AcDataCleanup } from "./DataCleanup";
import { AcLeakLookup } from "./LeakLookup";
import { AcNoBidFollowup } from "./NoBidAuctions";

// ============================================================
// VIEW: ADMIN CONTROLS
// Central control room — signup trend, KYC approval queue, payment
// hold/release, platform revenue/GMV chart, disputes summary.
// ============================================================
export function AdminControls({ initialTab = "dashboard" }) {
  const [tab, setTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const loadSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dashboard`);
      setSummary(data);
    } catch {}
  };
  useEffect(() => { loadSummary(); }, [refresh]);

  const tabs = [
    { id: "dashboard", label: "Overview" },
    { id: "registrations", label: "Registrations" },
    { id: "partner-apps", label: `Partner apps${summary?.partner_apps?.pending ? ` · ${summary.partner_apps.pending}` : ""}` },
    { id: "kyc", label: `KYC queue${summary?.kyc?.pending ? ` · ${summary.kyc.pending}` : ""}` },
    { id: "payouts", label: `Pending payouts${summary?.payouts?.pending ? ` · ${summary.payouts.pending}` : ""}` },
    { id: "sample-packs", label: "Sample packs" },
    { id: "audit", label: "Audit log" },
    { id: "disputes", label: "Disputes" },
    { id: "conversations", label: "Conversations" },
    { id: "support", label: `Support${summary?.support?.unread ? ` · ${summary.support.unread}` : ""}` },
    { id: "fix-ownership", label: "Fix job ownership" },
    { id: "no-bid", label: "No-bid follow-up" },
    { id: "test-accounts", label: "Test accounts" },
    { id: "data-cleanup", label: "Data cleanup" },
    { id: "leak-lookup", label: "Leak lookup" },
    { id: "settlements", label: "Settlements" },
    { id: "payments", label: `Payments${summary?.payments?.on_hold ? ` · ${summary.payments.on_hold} on hold` : ""}` },
    { id: "ledger", label: "GST ledger" },
    { id: "stats", label: "Revenue & GMV" },
  ];

  return (
    <section className="content" data-testid="admin-controls">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ADMIN · CONTROL ROOM</p>
          <h2>Every lever in one place</h2>
          <p className="subhead">Approve KYC, hold or release escrow payments, watch daily signups and platform revenue.</p>
        </div>
      </div>

      <nav className="ac-tabs" data-testid="ac-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`ac-tab-${t.id}`}
            className={`ac-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "dashboard" && <AcOverview summary={summary} onNav={setTab}/>}
      {tab === "registrations" && <AcRegistrations/>}
      {tab === "partner-apps" && <AcPartnerApps onChange={() => setRefresh(r => r + 1)}/>}
      {tab === "kyc" && <AcKycQueue onChange={() => setRefresh(r => r + 1)}/>}
      {tab === "payouts" && <AcPendingPayouts onChange={() => setRefresh(r => r + 1)}/>}
      {tab === "sample-packs" && <AcSamplePacks/>}
      {tab === "audit" && <AcAuditLog/>}
      {tab === "disputes" && <div className="ac-embed" data-testid="ac-disputes"><DisputesView/></div>}
      {tab === "conversations" && <div className="ac-embed" data-testid="ac-conversations"><AdminConversations role="admin"/></div>}
      {tab === "support" && <AcSupport/>}
      {tab === "fix-ownership" && <AcFixJobOwnership/>}
      {tab === "no-bid" && <AcNoBidFollowup/>}
      {tab === "test-accounts" && <AcTestAccounts/>}
      {tab === "data-cleanup" && <AcDataCleanup/>}
      {tab === "leak-lookup" && <AcLeakLookup/>}
      {tab === "settlements" && <AcSettlements onChange={() => setRefresh(r => r + 1)}/>}
      {tab === "payments" && <AcPayments onChange={() => setRefresh(r => r + 1)}/>}
      {tab === "ledger" && <AcLedger/>}
      {tab === "stats" && <AcPlatformStats/>}
    </section>
  );
}


export function AcOverview({ summary, onNav }) {
  if (!summary) return <div className="ac-panel" data-testid="ac-overview-loading">Loading dashboard…</div>;
  const cards = [
    { key: "signups", label: "Signups today", value: summary.users.signups_today, sub: `${summary.users.signups_yesterday} yesterday`, testid: "ac-card-signups", onClick: () => onNav("registrations") },
    { key: "kyc", label: "Pending KYC", value: summary.kyc.pending, sub: `${summary.kyc.verified_printers + summary.kyc.verified_agencies} approved`, testid: "ac-card-kyc", onClick: () => onNav("kyc") },
    { key: "escrow", label: "In escrow", value: summary.payments.in_escrow, sub: `${summary.payments.on_hold} on hold · ${summary.payments.released} released`, testid: "ac-card-escrow", onClick: () => onNav("payments") },
    { key: "disputes", label: "Open disputes", value: summary.disputes.open, sub: `${summary.disputes.resolved} resolved`, testid: "ac-card-disputes" },
    { key: "jobs", label: "Awaiting review", value: summary.jobs.pending_review, sub: `${summary.jobs.live} live · ${summary.jobs.awarded} awarded`, testid: "ac-card-jobs" },
    { key: "users", label: "Total users", value: summary.users.total, sub: `${summary.users.buyers} buyers · ${summary.users.printers} printers · ${summary.users.agencies} agencies`, testid: "ac-card-users" },
  ];
  return (
    <div className="ac-grid" data-testid="ac-overview">
      {cards.map(c => (
        <button
          key={c.key}
          className={`ac-card${c.onClick ? " clickable" : ""}`}
          data-testid={c.testid}
          onClick={c.onClick}
          disabled={!c.onClick}
        >
          <span className="ac-card-label">{c.label}</span>
          <span className="ac-card-value">{c.value}</span>
          <span className="ac-card-sub">{c.sub}</span>
        </button>
      ))}
    </div>
  );
}


export function AcRegistrations() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [reviewUser, setReviewUser] = useState(null);  // user obj for plan-review modal
  const reload = () => axios.get(`${API}/admin/registrations?days=${days}`).then(r => setData(r.data)).catch(() => setData({ daily: [], recent: [] }));
  useEffect(() => { reload(); }, [days]); // eslint-disable-line
  if (!data) return <div className="ac-panel" data-testid="ac-registrations-loading">Loading…</div>;
  const maxCount = Math.max(1, ...data.daily.map(d => d.count));
  return (
    <div className="ac-panel" data-testid="ac-registrations">
      <header className="ac-panel-head">
        <b>Daily user registrations</b>
        <div className="ac-controls">
          {[7, 14, 30, 90].map(d => (
            <button key={d} data-testid={`ac-reg-days-${d}`} className={`chip ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </header>
      <div className="ac-chart" data-testid="ac-reg-chart">
        {data.daily.map(d => (
          <div key={d.date} className="ac-bar" data-testid={`ac-reg-bar-${d.date}`}>
            <div className="ac-bar-fill" style={{ height: `${(d.count / maxCount) * 100}%` }} title={`${d.date} · ${d.count}`}/>
            <span className="ac-bar-count">{d.count}</span>
            <span className="ac-bar-date">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="ac-role-strip">
        <b>By role ({days}d):</b>
        <span>Buyers: {data.by_role.buyer || 0}</span>
        <span>Printers: {data.by_role.printer || 0}</span>
        <span>Agencies: {data.by_role.agency || 0}</span>
      </div>
      <h4 className="ac-sub">Recent signups</h4>
      <table className="ac-table" data-testid="ac-reg-table">
        <thead>
          <tr><th>Name</th><th>Email</th><th>Role</th><th>Plan</th><th>Last payment</th><th>Verified</th><th>Joined</th><th></th></tr>
        </thead>
        <tbody>
          {data.recent.slice(0, 25).map(u => {
            const roleTiers = {
              buyer: ["free_buyer", "growth", "enterprise"],
              printer: ["starter_free", "pro", "enterprise"],
              agency: ["free_partner", "studio_pro", "studio_enterprise"],
            }[u.role] || [];
            const invalid = u.subscription_tier && roleTiers.length > 0 && !roleTiers.includes(u.subscription_tier);
            return (
              <tr key={u.id} data-testid={`ac-reg-row-${u.id}`}>
                <td>{u.name || "—"}</td>
                <td>{u.email}</td>
                <td><span className={`chip role-${u.role}`}>{u.role}</span></td>
                <td>
                  <span className={`chip tier-${u.subscription_tier || "none"}`} data-testid={`tier-${u.id}`} style={invalid ? {background:"#FEE2E2",color:"#991B1B"} : {}}>
                    {u.subscription_tier || "—"}{invalid ? " ⚠" : ""}
                  </span>
                </td>
                <td data-testid={`last-pay-${u.id}`}>
                  {u.last_payment ? (
                    <span style={{font:"600 12px 'DM Mono'",color:"#059669"}}>₹{u.last_payment.amount_inr.toLocaleString("en-IN")}</span>
                  ) : <span className="muted">—</span>}
                </td>
                <td>{u.is_verified ? <CheckCircle2 size={14}/> : <span className="muted">—</span>}</td>
                <td className="muted">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}</td>
                <td>
                  {u.role !== "admin" && (
                    <button
                      data-testid={`review-plan-${u.id}`}
                      className="secondary-btn"
                      style={{padding:"4px 10px",fontSize:11}}
                      onClick={() => setReviewUser(u)}
                    >
                      {u.last_payment ? "Review & set plan" : "Set plan"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {reviewUser && (
        <PlanReviewModal
          user={reviewUser}
          close={() => setReviewUser(null)}
          onSaved={() => { setReviewUser(null); reload(); }}
        />
      )}
    </div>
  );
}


export function PlanReviewModal({ user, close, onSaved }) {
  const [payments, setPayments] = useState(null);
  const [plans, setPlans] = useState([]);
  const [chosen, setChosen] = useState(user.subscription_tier || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/users/${user.id}/payments`).then(r => setPayments(r.data)).catch(() => setPayments({ orders: [] }));
    axios.get(`${API}/subscriptions/plans?role=${user.role}`).then(r => setPlans(r.data[user.role] || [])).catch(() => setPlans([]));
  }, [user.id, user.role]);
  const save = async () => {
    if (!chosen) { setErr("Pick a plan first"); return; }
    setBusy(true); setErr("");
    try {
      await axios.patch(`${API}/users/${user.id}/subscription`, { tier_id: chosen });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update plan");
    } finally { setBusy(false); }
  };
  const paidOrders = (payments?.orders || []).filter(o => o.status === "paid");
  const latestPaid = paidOrders[0];
  return (
    <div className="drawer-backdrop" onClick={close}>
      <aside className="drawer" onClick={e => e.stopPropagation()} data-testid="plan-review-modal">
        <button data-testid="plan-review-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">RECONCILE SUBSCRIPTION · ADMIN</p>
          <h2>{user.name || user.email}</h2>
          <p style={{color:"#64748b",font:"12px Outfit",marginTop:6}}>{user.email} · <span className={`chip role-${user.role}`}>{user.role}</span> · currently on <b data-testid="current-tier">{user.subscription_tier || "—"}</b></p>
        </div>

        <div className="drawer-section-title" style={{marginTop:14}}><h3>Payment history</h3><span>{paidOrders.length} paid</span></div>
        {!payments ? (
          <div className="pipeline-empty">Loading…</div>
        ) : paidOrders.length === 0 ? (
          <div className="pipeline-empty" data-testid="plan-review-no-payments">No successful subscription payments found.</div>
        ) : (
          <>
            <div data-testid="latest-paid-summary" style={{padding:14,background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:8,display:"flex",flexDirection:"column",gap:6}}>
              <span style={{font:"10px 'DM Mono'",color:"#166534",letterSpacing:.5,textTransform:"uppercase"}}>LATEST SUCCESSFUL PAYMENT</span>
              <div style={{display:"flex",alignItems:"baseline",gap:12,flexWrap:"wrap"}}>
                <b style={{font:"700 24px Outfit",color:"#059669"}} data-testid="latest-paid-amount">₹{latestPaid.amount_inr.toLocaleString("en-IN")}</b>
                <span style={{font:"11px 'DM Mono'",color:"#64748b"}}>{latestPaid.paid_tier && `tier field: ${latestPaid.paid_tier}`}</span>
              </div>
              <span style={{font:"11px Outfit",color:"#475569"}}>
                Order <code>{latestPaid.razorpay_order_id}</code> · Payment <code>{latestPaid.razorpay_payment_id || "—"}</code>
              </span>
              {latestPaid.base_inr != null && (
                <span style={{font:"11px Outfit",color:"#475569"}}>
                  Breakdown: ₹{latestPaid.base_inr?.toLocaleString("en-IN")} base + ₹{latestPaid.gst_amount_inr?.toLocaleString("en-IN")} GST ({latestPaid.gst_pct}%)
                </span>
              )}
              <span style={{font:"11px Outfit",color:"#475569"}}>
                Captured: {latestPaid.verified_at ? new Date(latestPaid.verified_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
              </span>
            </div>
            {paidOrders.length > 1 && (
              <details style={{marginTop:10}}>
                <summary style={{cursor:"pointer",font:"600 12px Outfit",color:"#475569"}}>Older payments ({paidOrders.length - 1})</summary>
                <ul style={{listStyle:"none",padding:0,margin:"8px 0 0",display:"flex",flexDirection:"column",gap:6}}>
                  {paidOrders.slice(1).map(o => (
                    <li key={o.razorpay_order_id} style={{padding:"8px 10px",background:"#F8FAFC",border:"1px solid var(--line)",borderRadius:6,font:"12px 'DM Mono'",color:"#334155"}}>
                      ₹{o.amount_inr.toLocaleString("en-IN")} · tier={o.tier} · {o.razorpay_order_id} · {o.verified_at ? new Date(o.verified_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        <div className="drawer-section-title" style={{marginTop:20}}><h3>Set plan manually</h3><span>Admin override</span></div>
        <div className="plan-radio-grid" data-testid="plan-radio-grid">
          {plans.map(p => (
            <label key={p.id} className={`plan-radio ${chosen === p.id ? "active" : ""}`} data-testid={`plan-radio-${p.id}`}>
              <input type="radio" name="plan" value={p.id} checked={chosen === p.id} onChange={() => setChosen(p.id)}/>
              <div className="plan-radio-body">
                <b>{p.name}</b>
                <span className="plan-radio-price">
                  {p.billing.total_inr > 0 ? (
                    <>₹{p.billing.total_inr.toLocaleString("en-IN")}<em> incl. GST</em></>
                  ) : "Free"}
                </span>
                <span className="plan-radio-base">
                  {p.billing.total_inr > 0 && <>Base ₹{p.billing.base_inr.toLocaleString("en-IN")} + ₹{p.billing.gst_amount_inr.toLocaleString("en-IN")} GST</>}
                </span>
              </div>
            </label>
          ))}
        </div>
        {err && <div style={{padding:10,margin:"10px 0",background:"#FEF2F2",border:"1px solid #FCA5A5",color:"#991B1B",borderRadius:6,fontSize:12}} data-testid="plan-review-err">{err}</div>}
        <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
          <button className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="plan-review-save" className="primary-btn" onClick={save} disabled={busy || !chosen}>
            {busy ? "Saving…" : "Apply plan to user"} <CheckCircle2 size={15}/>
          </button>
        </div>
      </aside>
    </div>
  );
}


// ============================================================
// ADMIN · Payments / GST Ledger
// ============================================================
export function AcLedger() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("paid");
  const [email, setEmail] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [incTest, setIncTest] = useState(false);
  const load = async () => {
    const qs = new URLSearchParams({ days, include_test: incTest });
    if (kind) qs.set("kind", kind);
    if (status) qs.set("status", status);
    if (email) qs.set("user_email", email);
    try {
      const { data } = await axios.get(`${API}/admin/payments?${qs.toString()}`);
      setData(data);
    } catch (e) {
      setData({ count: 0, orders: [], totals_by_kind: {}, grand_total_inr: 0 });
    }
  };
  useEffect(() => { load(); }, [days, kind, status, email, incTest]); // eslint-disable-line
  const applyEmail = () => setEmail(emailDraft.trim());
  const exportCsv = () => {
    if (!data?.orders?.length) return;
    const headers = ["created_at", "razorpay_order_id", "razorpay_payment_id", "status", "kind", "user_email", "user_name", "user_role", "amount_inr", "base_inr", "gst_amount_inr", "gst_pct", "tier", "job_id", "brief_id"];
    const rows = data.orders.map(o => headers.map(h => {
      if (h === "user_email") return o.user?.email || "";
      if (h === "user_name") return o.user?.name || "";
      if (h === "user_role") return o.user?.role || "";
      const v = o[h];
      if (v == null) return "";
      return String(v).replace(/,/g, " ");
    }).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `goprintpack-payments-${days}d-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  if (!data) return <div className="ac-panel" data-testid="ac-ledger-loading">Loading ledger…</div>;
  const totals = Object.entries(data.totals_by_kind || {});
  return (
    <div className="ac-panel" data-testid="ac-ledger">
      <header className="ac-panel-head">
        <b>Payments ledger · GST reconciliation</b>
        <div className="ac-controls" style={{flexWrap:"wrap",gap:6}}>
          <IncludeTestToggle value={incTest} onChange={setIncTest} count={data.test_orders} testid="ledger-include-test"/>
          <select data-testid="ledger-kind" value={kind} onChange={e => setKind(e.target.value)} style={{padding:"4px 8px",border:"1px solid var(--line)",borderRadius:5,font:"11px 'DM Mono'"}}>
            <option value="">All kinds</option>
            <option value="subscription">Subscription</option>
            <option value="job_escrow">Print escrow</option>
            <option value="escrow">Escrow (legacy)</option>
            <option value="design_brief_escrow">Design escrow</option>
          </select>
          <select data-testid="ledger-status" value={status} onChange={e => setStatus(e.target.value)} style={{padding:"4px 8px",border:"1px solid var(--line)",borderRadius:5,font:"11px 'DM Mono'"}}>
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="created">Created (abandoned)</option>
            <option value="failed">Failed</option>
          </select>
          {[7, 30, 90, 180, 365].map(d => (
            <button key={d} data-testid={`ledger-days-${d}`} className={`chip ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </header>
      <div style={{display:"flex",gap:8,alignItems:"center",padding:"8px 0",flexWrap:"wrap"}}>
        <input
          data-testid="ledger-email"
          placeholder="Filter by exact email (optional)"
          value={emailDraft}
          onChange={e => setEmailDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") applyEmail(); }}
          style={{flex:1,minWidth:220,padding:"6px 10px",border:"1px solid var(--line)",borderRadius:5,font:"12px Outfit"}}
        />
        <button data-testid="ledger-email-apply" className="secondary-btn" style={{padding:"6px 12px",fontSize:12}} onClick={applyEmail}>Filter</button>
        {email && <button data-testid="ledger-email-clear" className="secondary-btn" style={{padding:"6px 12px",fontSize:12}} onClick={() => { setEmail(""); setEmailDraft(""); }}>Clear</button>}
        <button data-testid="ledger-export-csv" className="primary-btn" style={{padding:"6px 14px",fontSize:12}} onClick={exportCsv} disabled={!data.orders?.length}>
          Export CSV
        </button>
      </div>

      <div className="ledger-totals" data-testid="ledger-totals">
        <div className="ledger-tot-card grand">
          <span>GRAND TOTAL · {days}d</span>
          <b>₹{data.grand_total_inr.toLocaleString("en-IN")}</b>
          <em>{data.count} orders scanned</em>
        </div>
        {totals.map(([k, t]) => (
          <div key={k} className="ledger-tot-card" data-testid={`ledger-tot-${k}`}>
            <span>{k.replace(/_/g, " ")}</span>
            <b>₹{t.total_inr.toLocaleString("en-IN")}</b>
            <em>{t.count} paid · GST ₹{t.gst_inr.toLocaleString("en-IN")}</em>
          </div>
        ))}
      </div>

      <table className="ac-table" data-testid="ledger-table" style={{marginTop:12}}>
        <thead>
          <tr>
            <th>Captured</th><th>Order / Payment</th><th>Kind</th><th>User</th><th>Status</th><th style={{textAlign:"right"}}>Amount</th><th style={{textAlign:"right"}}>GST</th>
          </tr>
        </thead>
        <tbody>
          {data.orders.length === 0 ? (
            <tr><td colSpan="7" style={{textAlign:"center",padding:20,color:"#64748b"}}>No orders in this window / filter.</td></tr>
          ) : data.orders.slice(0, 100).map(o => (
            <tr key={o.razorpay_order_id} data-testid={`ledger-row-${o.razorpay_order_id}`}>
              <td className="muted">{o.verified_at ? new Date(o.verified_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : new Date(o.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
              <td>
                <div style={{font:"11px 'DM Mono'",color:"#334155"}}>{o.razorpay_order_id}</div>
                {o.razorpay_payment_id && <div style={{font:"10px 'DM Mono'",color:"#64748b"}}>{o.razorpay_payment_id}</div>}
              </td>
              <td><span className={`chip tier-${o.kind}`}>{(o.kind || "—").replace(/_/g, " ")}</span></td>
              <td>
                {o.user ? (
                  <>
                    <div style={{font:"12px Outfit"}}>{o.user.name || "—"}</div>
                    <div className="muted" style={{font:"10px Outfit"}}>{o.user.email} · {o.user.role}</div>
                  </>
                ) : <span className="muted">{o.user_id || "—"}</span>}
              </td>
              <td><span className={`chip ${o.status === "paid" ? "role-admin" : "role-buyer"}`}>{o.status}</span></td>
              <td style={{textAlign:"right",font:"600 13px 'DM Mono'",color:o.status === "paid" ? "#059669" : "#64748b"}}>
                ₹{o.amount_inr.toLocaleString("en-IN")}
              </td>
              <td style={{textAlign:"right",font:"12px 'DM Mono'",color:"#64748b"}}>
                {o.gst_amount_inr ? `₹${Number(o.gst_amount_inr).toLocaleString("en-IN")}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.orders.length > 100 && (
        <p style={{textAlign:"center",color:"#64748b",font:"11px 'DM Mono'",padding:8}}>
          Showing newest 100 of {data.orders.length} orders · export CSV for the full list
        </p>
      )}
    </div>
  );
}
